"use client";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { TriangleAlertIcon } from "lucide-react";
import { toast } from "sonner";
import {
  deleteIngredient,
  type DeleteIngredientResult,
} from "@/app/actions/ingredients/deleteIngredient";
import { UnderlineLink } from "@/components/primitives/underline-link";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
type DeleteIngredientDialogProps = {
  ingredient: {
    id: number;
    name: string;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
  deleteIngredientAction?: DeleteIngredientAction;
};
export type DeleteIngredientAction = (
  options: Parameters<typeof deleteIngredient>[0],
) => ReturnType<typeof deleteIngredient>;
type IngredientUsageRecipe = Extract<
  DeleteIngredientResult,
  {
    deleted: false;
  }
>["recipes"][number];
export function DeleteIngredientDialog({
  ingredient,
  open,
  onOpenChange,
  onDeleted,
  deleteIngredientAction = deleteIngredient,
}: DeleteIngredientDialogProps) {
  const [requiresForce, setRequiresForce] = useState(false);
  const [usageRecipes, setUsageRecipes] = useState<IngredientUsageRecipe[]>([]);
  const deletion = useMutation({
    mutationFn: deleteIngredientAction,
    onSuccess: (result) => {
      if (!result.deleted) {
        setUsageRecipes(result.recipes);
        setRequiresForce(true);
        return;
      }
      toast.success("Ingredient deleted successfully");
      setRequiresForce(false);
      setUsageRecipes([]);
      onOpenChange(false);
      onDeleted();
    },
    onError: () => {
      toast.error("Failed to delete ingredient. Please try again.");
    },
  });
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setRequiresForce(false);
      setUsageRecipes([]);
      deletion.reset();
    }
    onOpenChange(nextOpen);
  };
  const handleDelete = () => {
    deletion.mutate({
      data: requiresForce
        ? { ingredientId: ingredient.id, force: true }
        : { ingredientId: ingredient.id },
    });
  };
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {requiresForce ? "Ingredient used in recipes" : "Delete ingredient"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-1 text-sm">
          {requiresForce ? (
            <>
              <Alert variant="warning">
                <TriangleAlertIcon aria-hidden />
                <AlertTitle>Ingredient is used in recipes</AlertTitle>
                <AlertDescription>
                  Deleting <strong>{ingredient.name}</strong> will also remove
                  it from these recipes. This action cannot be undone.
                </AlertDescription>
              </Alert>
              {usageRecipes.length > 0 ? (
                <div className="mt-2 text-left text-sm">
                  <p className="font-medium">
                    Used in {usageRecipes.length}{" "}
                    {usageRecipes.length === 1 ? "recipe" : "recipes"}:
                  </p>
                  <ul
                    className="mt-2 max-h-48 list-disc space-y-1 overflow-y-auto pl-5"
                    aria-label="Recipes using this ingredient"
                  >
                    {usageRecipes.map((recipe) => (
                      <li key={recipe.slug}>
                        <UnderlineLink to={`/recipes/${recipe.slug}`}>
                          {recipe.name}
                        </UnderlineLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <p>
                Are you sure you want to delete{" "}
                <strong>{ingredient.name}</strong>?
              </p>
              <p>This action cannot be undone.</p>
            </>
          )}
        </div>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>
            Cancel
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleDelete}
            busy={deletion.isPending}
          >
            {requiresForce ? "Delete ingredient anyway" : "Delete ingredient"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
